import React from 'react'
import { Button } from 'react-aria-components'

import { css, sva } from '@portal/styled-system/css'

import { type Translation, useI18n } from '@/shared/lib/i18n'
import { CancelButton, Modal } from '@/shared/ui'

import { reasonButtonRecipe } from '../recipe'
import type { MessageReportDialogProps } from './view'

type ReportReason = {
    code: string
    text: (i18n: Translation) => string
    icon: string
}

const reasons: ReportReason[] = [
    // Спам
    {
        code: 'spam',
        text: (i18n) => i18n.CHAT_REASON_SPAM,
        icon: 'fa-regular fa-trash',
    },

    // Токсичное поведение
    {
        code: 'toxic_behavior',
        text: (i18n) => i18n.CHAT_REASON_TOXIC,
        icon: 'fa-regular fa-biohazard',
    },

    // Откровенный контент
    {
        code: 'inappropriate_content',
        text: (i18n) => i18n.CHAT_REASON_NSFW,
        icon: 'fa-regular fa-face-hand-peeking',
    },

    // Дезинформация
    {
        code: 'misinformation',
        text: (i18n) => i18n.CHAT_REASON_MISINFORMATION,
        icon: 'fa-regular fa-face-lying',
    },

    // Мошенничество
    {
        code: 'fraud',
        text: (i18n) => i18n.CHAT_REASON_FRAUD,
        icon: 'fa-regular fa-hand',
    },

    // Нарушение конфиденциальности данных
    {
        code: 'privacy_violation',
        text: (i18n) => i18n.CHAT_REASON_PRIVACY,
        icon: 'fa-regular fa-address-card',
    },

    // Нарушение правил игры
    {
        code: 'violation_of_game_rules',
        text: (i18n) => i18n.CHAT_REASON_RULES,
        icon: 'fa-regular fa-user-police',
    },

    // Другое
    {
        code: 'something_else',
        text: (i18n) => i18n.CHAT_REASON_OTHER,
        icon: 'fa-regular fa-info-circle',
    },
]

export function StepOne(props: MessageReportDialogProps) {
    const { i18n } = useI18n()

    const classes = reportReasonListRecipe()

    return (
        <React.Fragment>
            <Modal.Body styles={css.raw({ gap: '3', paddingBottom: '3' })}>
                <div className={classes.notice}>
                    <i className="fa-solid fa-circle-info" />
                    {i18n.CHAT_REPORT_STEP01_NOTICE}
                </div>
                <span className={classes.description}>{i18n.CHAT_REPORT_STEP01_DESCR}</span>
                <div className={classes.reasonButtonList}>
                    {reasons.map((reason) => (
                        <Button
                            key={reason.code}
                            className={reasonButtonRecipe()}
                            onPress={() => props.onReportReasonClick({ reason_code: reason.code })}
                        >
                            <i className={`${reason.icon} fa-fw`} />
                            {reason.text(i18n)}
                        </Button>
                    ))}
                </div>
            </Modal.Body>
            <Modal.Buttons
                styles={css.raw({
                    justifyContent: 'flex-end',
                    paddingTop: '3',
                    paddingBottom: '3',
                    borderTop: 'default',
                    borderTopColor: 'gray-v2.300',
                })}
            >
                <CancelButton styles={css.raw({ width: 'auto' })} onPress={props.onClose} />
            </Modal.Buttons>
        </React.Fragment>
    )
}

const reportReasonListRecipe = sva({
    slots: ['notice', 'description', 'reasonButtonList'],
    base: {
        notice: {
            width: 'full',
            display: 'flex',
            alignItems: 'center',
            gap: '2.5',
            border: 'default',
            borderRadius: 'medium',
            paddingX: '3',
            paddingY: '2',
            fontFamily: 'notoSans',
            textStyle: 'tech-text-medium',
            color: 'primary.800',
            backgroundColor: 'primary.100',
            borderColor: 'primary.200',
        },
        description: {
            color: 'gray-v2.900',
            textStyle: 'header-five',
            fontWeight: 'normal',
            textAlign: 'start',
        },
        reasonButtonList: {
            width: 'full',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1',
        },
    },
})
